import { useState } from 'react';
import { type Attachment } from '@clawlet/gateway-client';
import {
  THEME_PRESETS,
  useSettingsStore,
  type ThemeColors,
  type ThemePreset,
} from '../../stores/settingsStore';
import ThemeBuilderChat from './ThemeBuilderChat';

interface AppearanceTabProps {
  sendMessage: (text: string, attachments?: Attachment[]) => Promise<void>;
}

const REQUIRED_THEME_KEYS: (keyof ThemeColors)[] = [
  'background',
  'foreground',
  'card',
  'card-foreground',
  'secondary',
  'secondary-foreground',
  'muted',
  'muted-foreground',
  'primary',
  'primary-hover',
  'primary-foreground',
  'accent',
  'accent-foreground',
  'destructive',
  'destructive-foreground',
  'destructive-border',
  'success',
  'warning',
  'border',
  'input',
  'ring',
  'ring-light',
  'primary-90',
  'primary-20',
  'ring-70',
  'scrollbar-thumb',
  'scrollbar-thumb-hover',
  'banner-bg',
  'user-bubble-background',
  'user-bubble-text',
  'assistant-bubble-background',
  'assistant-bubble-text',
];

const TEXTAREA_CLASSES =
  'w-full h-80 bg-card border border-input rounded-lg text-foreground text-sm px-4 py-3 font-mono focus:border-ring outline-none resize-y';

const TOGGLE_BASE_CLASSES = 'rounded-lg border border-input px-3 py-2 text-sm transition-colors';

function ThemeCard({
  preset,
  isActive,
  onSelect,
}: {
  preset: ThemePreset;
  isActive: boolean;
  onSelect: () => void;
}) {
  const { colors } = preset;
  const swatches = [
    colors.background,
    colors.card,
    colors.primary,
    colors['accent-foreground'],
    colors.foreground,
  ];

  return (
    <button
      type="button"
      onClick={onSelect}
      className={`relative rounded-xl border-2 p-3 text-left transition-all ${
        isActive ? 'border-ring ring-2 ring-ring' : 'border-border hover:border-input'
      }`}
    >
      <div className="mb-2.5 flex h-8 gap-1 overflow-hidden rounded-lg">
        {swatches.map((color, i) => (
          <div key={i} className="h-full flex-1" style={{ backgroundColor: color }} />
        ))}
      </div>

      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-foreground">{preset.name}</span>
        {isActive && (
          <svg
            className="h-4 w-4 text-ring"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="20 6 9 17 4 12" />
          </svg>
        )}
      </div>
    </button>
  );
}

function validateThemeJson(text: string): { theme: ThemePreset; error: null } | { theme: null; error: string } {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    return { theme: null, error: "This theme file isn't valid JSON." };
  }

  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return { theme: null, error: "This theme file isn't in the right format." };
  }

  const obj = parsed as Record<string, unknown>;

  if (typeof obj.name !== 'string' || !obj.name.trim()) {
    return { theme: null, error: 'This theme needs a name before it can be saved.' };
  }

  if (!obj.colors || typeof obj.colors !== 'object' || Array.isArray(obj.colors)) {
    return { theme: null, error: 'This theme is missing its color settings.' };
  }

  const colors = obj.colors as Record<string, unknown>;
  const missing: string[] = [];
  for (const key of REQUIRED_THEME_KEYS) {
    if (typeof colors[key] !== 'string') {
      missing.push(key);
    }
  }

  if (missing.length > 0) {
    return { theme: null, error: 'This theme is missing some required colors.' };
  }

  return {
    theme: {
      id: '', // caller sets this
      name: obj.name.trim(),
      colors: colors as unknown as ThemeColors,
    },
    error: null,
  };
}

export default function AppearanceTab({ sendMessage }: AppearanceTabProps) {
  const settings = useSettingsStore((s) => s.settings);
  const setActiveTheme = useSettingsStore((s) => s.setActiveTheme);
  const updateCustomTheme = useSettingsStore((s) => s.updateCustomTheme);
  const addCustomTheme = useSettingsStore((s) => s.addCustomTheme);
  const activeTheme = settings.appearance.activeTheme;

  const [jsonEditorOpen, setJsonEditorOpen] = useState(false);
  const [jsonText, setJsonText] = useState('');
  const [jsonError, setJsonError] = useState<string | null>(null);

  const allThemes = [...THEME_PRESETS, ...settings.appearance.savedCustomThemes];
  const activePreset = allThemes.find((t) => t.id === activeTheme);
  const isBuiltinPreset = THEME_PRESETS.some((t) => t.id === activeTheme);

  const openJsonEditor = () => {
    if (!activePreset) return;
    const { id: _id, ...rest } = activePreset;
    setJsonText(JSON.stringify(rest, null, 2));
    setJsonError(null);
    setJsonEditorOpen(true);
  };

  const toggleJsonEditor = () => {
    if (jsonEditorOpen) {
      setJsonEditorOpen(false);
      setJsonError(null);
      return;
    }
    openJsonEditor();
  };

  const applyJson = () => {
    const result = validateThemeJson(jsonText);
    if (result.error || !result.theme) {
      setJsonError(result.error ?? 'Something went wrong. Please try again.');
      return;
    }

    const theme = result.theme;

    if (isBuiltinPreset) {
      // Fork: save as new custom theme
      const newTheme: ThemePreset = {
        id: `custom-${Date.now()}`,
        name: theme.name,
        colors: theme.colors,
      };
      addCustomTheme(newTheme);
    } else {
      // Update existing custom theme in place
      updateCustomTheme(activeTheme, { id: activeTheme, name: theme.name, colors: theme.colors });
    }

    setJsonError(null);
    setJsonEditorOpen(false);
  };

  return (
    <div className="space-y-6">
      <div>
        <h4 className="mb-1 text-sm font-semibold text-foreground">Theme</h4>
        <p className="mb-4 text-xs text-muted-foreground">
          Choose a color theme for the interface. Changes apply instantly.
        </p>

        <div className="grid grid-cols-3 gap-3">
          {allThemes.map((theme) => (
            <ThemeCard
              key={theme.id}
              preset={theme}
              isActive={activeTheme === theme.id}
              onSelect={() => setActiveTheme(theme.id)}
            />
          ))}
        </div>
      </div>

      <div className="border-t border-border pt-6">
        <h4 className="mb-1 text-sm font-semibold text-foreground">Edit Theme</h4>
        <p className="mb-3 text-xs text-muted-foreground">
          Edit the active theme&apos;s colors as JSON.{' '}
          {isBuiltinPreset ? 'Saving will create a new custom theme (built-in presets are read-only).' : 'Changes update the theme in place.'}
        </p>

        <button
          type="button"
          onClick={toggleJsonEditor}
          className={`${TOGGLE_BASE_CLASSES} ${
            jsonEditorOpen
              ? 'bg-accent text-accent-foreground'
              : 'bg-secondary text-secondary-foreground hover:bg-muted'
          }`}
        >
          {jsonEditorOpen ? 'Close Theme (JSON)' : 'Open Theme (JSON)'}
        </button>

        {jsonEditorOpen && (
          <div className="mt-3">
            <textarea
              value={jsonText}
              onChange={(event) => setJsonText(event.target.value)}
              className={TEXTAREA_CLASSES}
              spellCheck={false}
            />

            <div className="mt-3 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={applyJson}
                className="rounded-lg bg-primary px-3 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary-hover"
              >
                {isBuiltinPreset ? 'Save as Custom Theme' : 'Apply'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setJsonEditorOpen(false);
                  setJsonError(null);
                }}
                className="rounded-lg border border-input bg-secondary px-3 py-2 text-sm text-secondary-foreground transition-colors hover:bg-muted"
              >
                Cancel
              </button>
            </div>

            {jsonError && <p className="mt-2 text-xs text-destructive-foreground">{jsonError}</p>}
          </div>
        )}
      </div>

      <div className="border-t border-border pt-6">
        <h4 className="mb-1 text-sm font-semibold text-foreground">AI Theme Builder</h4>
        <p className="mb-3 text-xs text-muted-foreground">
          Chat with AI to create custom themes from screenshots or descriptions.
        </p>
        <ThemeBuilderChat sendMessage={sendMessage} />
      </div>

      {settings.appearance.tempTheme && (
        <div className="border-t border-border pt-4">
          <h4 className="mb-1 text-sm font-semibold text-foreground">Preview Theme</h4>
          <p className="mb-3 text-xs text-muted-foreground">
            Generated by AI Theme Builder. Save it or discard to revert.
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => {
                const name = prompt('Theme name:');
                if (name?.trim()) {
                  useSettingsStore.getState().saveTempThemeAs(name.trim());
                }
              }}
              className="rounded-lg bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground transition-colors hover:bg-primary-hover"
            >
              Save Theme
            </button>
            <button
              type="button"
              onClick={() => useSettingsStore.getState().setTempTheme(null)}
              className="rounded-lg border border-input px-3 py-1.5 text-xs text-card-foreground transition-colors hover:border-muted-foreground"
            >
              Discard
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
